/* eslint-disable complexity */
import { Core } from "@faire/design-tokens";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackHelpCenterInstantAnswerPrimaryCtaClick } from "@faire/web-api--source/events/helpCenter/click/instantAnswerPrimaryCta";
import { trackHelpCenterInstantAnswerSecondaryCtaClick } from "@faire/web-api--source/events/helpCenter/click/instantAnswerSecondaryCta";
import kebabCase from "lodash/kebabCase";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

import { CONTACT_CARDS_COMBINED_WIDTH } from "../consts";
import { InstantAnswer, InstantAnswerContext } from "../types";

import { InstantAnswerExplanation } from "./InstantAnswerExplanation/InstantAnswerExplanation";

export interface IInstantAnswerProps extends InstantAnswerContext {
  instantAnswer: InstantAnswer;
  viewHasHelpCenterArticles?: boolean;
  issueType?: string;
  loadTimingRef?: React.RefCallback<HTMLElement>;
}

export const InstantAnswerCard: React.FC<IInstantAnswerProps> = ({
  instantAnswer,
  viewHasHelpCenterArticles,
  issueType,
  loadTimingRef,
  ...context
}) => {
  const {
    titleBuilder,
    descriptionBuilder,
    primaryCtaBuilder,
    secondaryCtaBuilder,
    learnMoreLink,
    linkTextBuilder,
  } = instantAnswer;

  const history = useHistory();

  const title = titleBuilder?.(context);
  const primaryCtaText = primaryCtaBuilder?.textBuilder?.(context);
  const primaryCtaLink = primaryCtaBuilder?.linkBuilder?.(context);
  const primaryCtaOnClick =
    primaryCtaBuilder?.onClickFunctionBuilder?.(context);
  const secondaryCtaText = secondaryCtaBuilder?.textBuilder?.(context);
  const secondaryCtaLink = secondaryCtaBuilder?.linkBuilder?.(context);
  const secondaryCtaOnClick =
    secondaryCtaBuilder?.onClickFunctionBuilder?.(context);
  const additionalComponent = linkTextBuilder?.(context);

  const retailerToken = context.retailer?.token ?? "";
  const brandToken = context.brand?.token ?? "";

  const handlePrimaryCtaClick = () => {
    primaryCtaOnClick?.(history);
    trackHelpCenterInstantAnswerPrimaryCtaClick(
      primaryCtaText ?? "",
      issueType ?? "",
      retailerToken,
      brandToken
    );
  };

  const handleSecondaryCtaClick = () => {
    secondaryCtaOnClick?.(history);
    trackHelpCenterInstantAnswerSecondaryCtaClick(
      secondaryCtaText ?? "",
      issueType ?? "",
      retailerToken,
      brandToken
    );
  };

  return (
    <Wrapper ref={loadTimingRef}>
      <Column>
        {title ? (
          <>
            <Typography
              data-test-id={kebabCase(title)}
              variant="sectionHeaderSansMedium"
            >
              {title}
            </Typography>
            <Spacer height="2x" />
          </>
        ) : null}

        {descriptionBuilder ? (
          <>
            <InstantAnswerExplanation
              descriptionParagraphs={descriptionBuilder(context)}
              viewHasHelpCenterArticles={!!viewHasHelpCenterArticles}
            />
          </>
        ) : null}

        {primaryCtaText || secondaryCtaText || learnMoreLink ? (
          <Spacer height="3x" />
        ) : null}

        <Row>
          {primaryCtaText && (primaryCtaLink || primaryCtaOnClick) ? (
            <>
              <PrimaryInstantAnswerButton
                linkTo={primaryCtaLink}
                onClick={handlePrimaryCtaClick}
                data-test-id={`${kebabCase(primaryCtaText)}-btn`}
              >
                {primaryCtaText}
              </PrimaryInstantAnswerButton>
              <Spacer height="3x" />
            </>
          ) : null}
          {secondaryCtaText && (secondaryCtaLink || secondaryCtaOnClick) ? (
            <>
              <SecondaryInstantAnswerButton
                linkTo={secondaryCtaLink}
                onClick={handleSecondaryCtaClick}
                data-test-id={`${kebabCase(secondaryCtaText)}-btn`}
              >
                {secondaryCtaText}
              </SecondaryInstantAnswerButton>
              <Spacer height="3x" />
            </>
          ) : null}
          {learnMoreLink ? (
            <>
              <Button
                variant={
                  primaryCtaText || secondaryCtaText ? "tertiary" : "primary"
                }
                linkTo={learnMoreLink}
                target="_blank"
              >
                <StrictLocalMsg
                  defaultMessage="Learn more"
                  description={{
                    text: "CTA button learn more and links to a help center article",
                  }}
                />
              </Button>
            </>
          ) : null}
        </Row>

        {primaryCtaText || secondaryCtaText || learnMoreLink ? (
          <Spacer height="2x" />
        ) : null}

        {additionalComponent ? (
          <>
            <Spacer height="1x" />
            <Row>{additionalComponent}</Row>
          </>
        ) : null}
      </Column>
    </Wrapper>
  );
};

const Wrapper = styled(Row)`
  ${responsiveMediaQueries.mobile} {
    margin-top: ${getSpacing("1x")};
  }
  ${responsiveMediaQueries.tabletAndAbove} {
    max-width: ${CONTACT_CARDS_COMBINED_WIDTH};
  }
`;

const SmallViewportFriendlyButton = styled(Button)`
  min-height: 48px;
  height: auto;
`;

const PrimaryInstantAnswerButton = styled(SmallViewportFriendlyButton).attrs({
  target: "_blank",
})`
  padding: ${getSpacing("1x", "3x")};
  width: fit-content;
  &:active,
  &:focus {
    color: ${Core.text.inverse};
  }
`;

const SecondaryInstantAnswerButton = styled(SmallViewportFriendlyButton).attrs({
  variant: "secondary",
  target: "_blank",
})`
  padding: ${getSpacing("1x", "3x")};
  width: fit-content;
`;
